import UPBreadcrumbs from 'components/breadcrumbs';
import { UPBreadcrumbItem } from 'components/breadcrumbs/types';
import UPDomainSidebar from 'components/domain-sidebar';
import { UPDomainSidebarItem } from 'components/domain-sidebar/types';
import { ConfigurationDomain } from 'constants/ConfigurationDomain';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { setMenuStatusAction } from 'store/actions/menu-actions';
import { activeMenuItemSelector } from 'store/selectors/menu-selectors';
import { paritairComiteSelector } from 'store/selectors/paritair-comite-selectors';
import { GENERAL__EDIT, MENU__MANAGEMENT, PARITAIR_COMITE__ADD_PARITAIR_COMITE } from 'translations/constants';
import {
  buildDomainRoute,
  buildSubSectionRoute,
  CONFIGURATION_DOMAINS,
  DEFAULT_CONFIGURATION_DOMAIN,
  getConfigurationDomain,
  getDomainKeyFromPath,
  getSubSectionKeyFromPath,
  isConfigurationDomain,
} from './domains';
import { getParitairComiteDetailsIdFromPath, NEW_PARITAIR_COMITE_ID } from './joint-commissions/routes';
import { getParitairComiteName } from './joint-commissions/utils';
import {
  ManagementBreadcrumbsRow,
  ManagementContainer,
  ManagementContent,
  ManagementFlatPanel,
  ManagementPanel,
} from './styles';

const DOMAIN_TEST_ID_PREFIX = 'management-domain-item';

/**
 * Shell of the Management section: the configuration-domain sidebar plus the active domain's
 * content. Domain content is mounted by each domain's own story as a child route.
 */
const Management = (): JSX.Element => {
  const [t, i18n] = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const activeItem = useAppSelector(activeMenuItemSelector);
  const paritairComite = useAppSelector(paritairComiteSelector);

  const domainKey = getDomainKeyFromPath(location.pathname);
  const subSectionKey = getSubSectionKeyFromPath(location.pathname);

  useEffect(() => {
    if (activeItem !== t(MENU__MANAGEMENT)) {
      dispatch(setMenuStatusAction(t(MENU__MANAGEMENT)));
    }
  }, [activeItem, dispatch, t]);

  const items: UPDomainSidebarItem[] = useMemo(
    () =>
      CONFIGURATION_DOMAINS.map(domain => ({
        key: domain.key,
        label: t(domain.labelKey),
        route: buildDomainRoute(domain.key),
      })),
    [t],
  );

  const breadcrumbs: UPBreadcrumbItem[] = useMemo(() => {
    const activeDomain = items.find(item => item.key === domainKey);
    const crumbs: UPBreadcrumbItem[] = [{ key: 'management', label: t(MENU__MANAGEMENT) }];
    if (activeDomain) {
      crumbs.push({ key: activeDomain.key, label: activeDomain.label, route: activeDomain.route });
      // A domain with tabs adds the active tab; the domain crumb then links back to the domain's default tab.
      const subSection = getConfigurationDomain(activeDomain.key as ConfigurationDomain)?.subSections?.find(
        section => section.key === subSectionKey,
      );
      if (subSection) {
        crumbs.push({
          key: `${activeDomain.key}-${subSection.key}`,
          label: t(subSection.labelKey),
          route: buildSubSectionRoute(activeDomain.key as ConfigurationDomain, subSection.key),
        });
      }
      const paritairComiteId = getParitairComiteDetailsIdFromPath(location.pathname);
      if (paritairComiteId === NEW_PARITAIR_COMITE_ID) {
        crumbs.push({ key: `${activeDomain.key}-details`, label: t(PARITAIR_COMITE__ADD_PARITAIR_COMITE) });
      } else if (paritairComiteId) {
        const name =
          paritairComite && String(paritairComite.id) === paritairComiteId
            ? getParitairComiteName(paritairComite, i18n.language)
            : '';
        crumbs.push({ key: `${activeDomain.key}-details`, label: [t(GENERAL__EDIT), name].filter(Boolean).join(' ') });
      }
    }
    return crumbs;
  }, [domainKey, subSectionKey, items, location.pathname, paritairComite, i18n.language, t]);

  const Panel =
    domainKey === ConfigurationDomain.CONTRACT_VALIDATION_COMPLIANCE ||
    domainKey === ConfigurationDomain.ABSENCE ||
    domainKey === ConfigurationDomain.SECURITY_AUTHENTICATION ||
    domainKey === ConfigurationDomain.TIME_REGISTRATION_POLICY ||
    domainKey === ConfigurationDomain.JOINT_COMMISSIONS
      ? ManagementFlatPanel
      : ManagementPanel;

  if (domainKey !== undefined && !isConfigurationDomain(domainKey)) {
    return <Navigate to={buildDomainRoute(DEFAULT_CONFIGURATION_DOMAIN)} replace />;
  }

  return (
    <ManagementContainer>
      <UPDomainSidebar items={items} activeKey={domainKey ?? ''} testIdPrefix={DOMAIN_TEST_ID_PREFIX} />
      <ManagementContent>
        <ManagementBreadcrumbsRow>
          <UPBreadcrumbs items={breadcrumbs} testId="management-breadcrumbs" />
        </ManagementBreadcrumbsRow>
        <Panel>
          <Outlet />
        </Panel>
      </ManagementContent>
    </ManagementContainer>
  );
};

export default Management;
